import { AuxiliumComponent } from "../auxilium-component";

export default class DateFormat extends AuxiliumComponent {
	static observable = ["date", "preset", "hint", "lang"];
	static observableBooleans = ["format", "relative", "capitalise"];

	#defaultCulture = "en-GB";
	#dateObject;

	#presets = {
		longdate: {
			year: "numeric",
			month: "long",
			day: "numeric"
		},
		longtime: {
			hour: "numeric",
			minute: "numeric",
			second: "numeric"
		},
		shortdate: {
			year: "numeric",
			month: "numeric",
			day: "numeric"
		},
		shorttime: {
			hour: "numeric",
			minute: "numeric"
		},
		shortdatetime: {
			year: "numeric",
			month: "numeric",
			day: "numeric",
			hour: "numeric",
			minute: "numeric"
		},
		longdatetime: {
			year: "numeric",
			month: "long",
			day: "numeric",
			hour: "numeric",
			minute: "numeric"
		},
		fulldate: {
			weekday: "long",
			year: "numeric",
			month: "long",
			day: "numeric"
		}
	}

	#now = new Date(Date.now());

	constructor() {
		super();

		this.#dateObject = this.date ? new Date(this.date) : this.#now;
		this.culture = this.lang || document.documentElement.dataset.culture || navigator.language || this.#defaultCulture;
	}

	#GetOptions() {
		if (this.preset) { return this.#presets[this.preset.toLowerCase()]; }

		return {};
	}

	#Round(num, threshold = 0.001) {
		if (num > 0) {
			return (num - Math.floor(num)) < 1 - threshold ? Math.floor(num) : Math.ceil(num);
		} else {
			return (Math.ceil(num) - num) < threshold ? Math.floor(num) : Math.ceil(num);
		}
	}

	#GetRelative() {
		const formatter = new Intl.RelativeTimeFormat(this.culture, { numeric: "auto" }),
			timeDifference = this.#dateObject - this.#now;

		const baseUnit = 1000;

		const seconds = this.#Round(timeDifference / baseUnit),
			minutes = this.#Round(timeDifference / (baseUnit * 60)),
			hours = this.#Round(timeDifference / (baseUnit * 3600)),
			days = this.#Round(timeDifference / (baseUnit * 86400), 0.0001),
			weeks = this.#Round(timeDifference / (baseUnit * 604800), 0.00001),
			years = this.#Round(timeDifference / (baseUnit * 31536000), 0.0000001);

		const yearsDiff = this.#dateObject.getFullYear() - this.#now.getFullYear();
		const monthsDiff = this.#dateObject.getMonth() - this.#now.getMonth();

		const months = yearsDiff * 12 + monthsDiff;

		if (Math.abs(seconds) < 60) {
			return Math.abs(seconds) < 1 ? formatter.format(0, 'second') : formatter.format(seconds, "seconds");
		} else if (Math.abs(minutes) < 60) {
			return formatter.format(minutes, 'minutes');
		} else if (Math.abs(hours) < 24) {
			return formatter.format(hours, 'hours');
		} else if (Math.abs(days) < 7) {
			return formatter.format(days, 'days');
		} else if (Math.abs(weeks) < 5) {
			return formatter.format(weeks, 'weeks');
		} else if (Math.abs(months) < 12) {
			return formatter.format(months, 'months');
		} else {
			return formatter.format(years, 'years');
		}
	}

	#process() {
		if (!this.relative) { this.innerHTML = new Intl.DateTimeFormat(this.culture, this.#GetOptions()).format(this.#dateObject); }
		else { this.innerHTML = this.#GetRelative(); }

		if (this.capitalise) { this.innerHTML = capitalise(this.innerHTML.trim()); }
		if (this.hint) {
			const hint = new Intl.DateTimeFormat(this.culture, this.#presets[this.hint.toLowerCase()]).format(this.#dateObject);

			if (hint !== this.innerHTML.trim()) {
				this.tooltip = hint;
			}
		}
	}

	parsedCallback() {
		this.#process();
	}

	attributeChanged(name, _, __) {
		this.#process();
	}
}
